// Página de inicio de sesión
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { Mail, Lock, LogIn, ArrowLeft, Download, X } from 'lucide-react'
import useAuth from '../hooks/useAuth'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { useQuery } from '@tanstack/react-query'
import client from '../api/client'

export default function Login() {
  const { handleLogin, isLoggingIn, loginError } = useAuth()
  const { register, handleSubmit, formState: { errors } } = useForm()

  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showiOSModal, setShowiOSModal] = useState(false)
  const [showAndroidModal, setShowAndroidModal] = useState(false)

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');

  useEffect(() => {
    if (isStandalone) {
      setIsInstalled(true);
    }

    const handlePromptAvailable = () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
      }
    };

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    };

    window.addEventListener('pwa-prompt-available', handlePromptAvailable);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt || window.deferredPrompt;
    if (!promptEvent) {
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isiOS) {
        setShowiOSModal(true);
      } else {
        setShowAndroidModal(true);
      }
      return;
    }
    
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      window.deferredPrompt = null;
      setDeferredPrompt(null);
    }
  };

  const showInstallButton = !isInstalled && !isStandalone;

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const logoSistema = settings?.logo_sistema || '/favicon.svg'
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const logoUrl = logoSistema.startsWith('/uploads') ? `${baseImgUrl}${logoSistema}` : (logoSistema.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoSistema.substring(1)}` : logoSistema)

  const onSubmit = (data) => {
    handleLogin(data)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'radial-gradient(ellipse at top, rgba(0, 194, 255, 0.12) 0%, transparent 60%), linear-gradient(180deg, #0A1F44 0%, #0D1B2A 100%)',
        position: 'relative',
      }}
    >
      {/* Patrón de fondo sutil */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.15,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '420px',
        }}
      >
        {/* Logo y título */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link to="/" style={{ display: 'inline-block', marginBottom: '16px' }} className="group">
            <img
              src={logoUrl}
              alt="Logo"
              style={{
                height: '48px',
                width: 'auto',
                maxWidth: '140px',
                objectFit: 'contain',
                margin: '0 auto',
              }}
              className="transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
            />
          </Link>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              fontSize: '1.75rem',
              color: '#F1F5F9',
              margin: '0 0 4px 0',
            }}
          >
            Bienvenido de vuelta
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#94A3B8', margin: 0 }}>
            Inicia sesión para ver tus predicciones
          </p>
        </div>

        {/* Formulario */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.07)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '32px',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Error global */}
            {loginError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  fontSize: '0.875rem',
                  color: '#EF4444',
                }}
              >
                {loginError}
              </motion.div>
            )}

            <Input
              label="Correo electrónico"
              type="email"
              icon={Mail}
              placeholder="tu@correo.com"
              error={errors.email?.message}
              {...register('email', {
                required: 'El correo es obligatorio',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo electrónico no válido',
                },
              })}
            />

            <Input
              label="Contraseña"
              type="password"
              icon={Lock}
              placeholder="••••••••"
              error={errors.password?.message}
              {...register('password', {
                required: 'La contraseña es obligatoria',
                minLength: {
                  value: 6,
                  message: 'Mínimo 6 caracteres',
                },
              })}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94A3B8', cursor: 'pointer' }}>
                <input type="checkbox" style={{ accentColor: '#00C2FF' }} />
                Recordarme
              </label>
              <a href="#" style={{ color: '#00C2FF', textDecoration: 'none', fontSize: '0.8125rem' }}>
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <Button
              type="submit"
              size="lg"
              loading={isLoggingIn}
              icon={LogIn}
              style={{ width: '100%', marginTop: '8px' }}
            >
              Iniciar sesión
            </Button>
          </form>

          {/* Separador */}
          <div style={{ position: 'relative', margin: '24px 0' }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '100%', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }} />
            </div>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <span style={{ padding: '0 12px', background: 'rgba(13, 27, 42, 0.8)', color: '#64748B', fontSize: '0.75rem' }}>
                o
              </span>
            </div>
          </div>

          {/* Enlace a registro */}
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: '#94A3B8', margin: 0 }}>
            ¿No tienes cuenta?{' '}
            <Link to="/registro" style={{ color: '#00C2FF', fontWeight: 500, textDecoration: 'none' }}>
              Regístrate gratis
            </Link>
          </p>

          {/* Botón de descarga de aplicación (visible si no está instalada) */}
          {showInstallButton && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleInstallClick}
                style={{
                  background: 'rgba(0, 194, 255, 0.1)',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  color: '#00C2FF',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 194, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 194, 255, 0.1)';
                }}
              >
                <Download style={{ width: '13px', height: '13px' }} /> Descargar Aplicación
              </button>
            </div>
          )}

          {/* Botón volver a la página principal */}
          <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '16px', textAlign: 'center' }}>
            <Link to="/" style={{ color: '#94A3B8', fontSize: '0.8125rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#00C2FF'} onMouseLeave={(e) => e.target.style.color = '#94A3B8'}>
              <ArrowLeft style={{ width: '14px', height: '14px' }} /> Volver a la página principal
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Modales de instrucciones de instalación */}
      {showiOSModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left">Instalar Aplicación en iOS</h3>
              <button onClick={() => setShowiOSModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary">
              <p>Sigue estos sencillos pasos para instalar <strong className="text-text-primary font-semibold">{nombreSistema}</strong> en tu iPhone o iPad:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs">
                <li>Presiona el botón de <strong>Compartir</strong> en la barra inferior de Safari (el icono de un cuadro con una flecha apuntando hacia arriba <span className="inline-block p-1 bg-white/10 rounded">📤</span>).</li>
                <li>Desplázate hacia abajo en el menú y selecciona la opción <strong>"Agregar a inicio"</strong> o <strong>"A la pantalla de inicio"</strong>.</li>
                <li>Presiona <strong>"Agregar"</strong> en la esquina superior derecha para confirmar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowiOSModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {showAndroidModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left">Instalar Aplicación</h3>
              <button onClick={() => setShowAndroidModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary">
              <p>Sigue estos pasos para instalar <strong className="text-text-primary font-semibold">{nombreSistema}</strong>:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs">
                <li>Presiona el botón de menú de tu navegador (los tres puntos en la esquina superior derecha).</li>
                <li>Selecciona la opción <strong>"Instalar aplicación"</strong> o <strong>"Agregar a la pantalla principal"</strong>.</li>
                <li>Confirma la acción para completar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowAndroidModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
